/**
 * useJulietteLauncher — Store partagé pour piloter l'ouverture de Juliette.
 *
 * Tous les points d'invocation (orbe sidebar, orbe mobile tab bar) ouvrent la
 * même surface : le panneau de conversation ancré à droite (JulietteRightPanel),
 * qui pousse le contenu. Expose aussi la conscience de page (`context`).
 *
 * Historique : les modes "widget" / "fullscreen-voice" / "desktop-expanded"
 * (V1→V3.5) ont été retirés avec leurs surfaces — il ne reste que "panel".
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  getJuliettePageContext,
  type JulietteContext,
} from "@/lib/juliette-page-context";

export type JulietteLauncherMode = "panel";

/** Onglets du panneau de conversation (miroir de JulietteRightPanel). */
export type JuliettePanelTab = "echange" | "signaux" | "suivi" | "historique";

const DEFAULT_TAB: JuliettePanelTab = "echange";

/**
 * Amorce de briefing : Juliette prend la parole des l'ouverture au lieu de dire
 * "je t'ecoute". `deck` = la file d'actions EXACTE affichee a l'ecran (cf.
 * buildDeckBriefing) ; `surface` = choix de l'agent dans la boite de dialogue.
 */
export interface JulietteKickoff {
  surface: "voice" | "text";
  deck: string;
}

interface JulietteLauncherState {
  isOpen: boolean;
  mode: JulietteLauncherMode;
  /** Onglet a afficher a l'ouverture (ex : "signaux" depuis le whisper CRM). */
  panelTab: JuliettePanelTab;
  /**
   * Nonce incremente a CHAQUE open(), meme si l'onglet demande est identique.
   * Permet au panneau de se re-caler sur panelTab a chaque re-clic du whisper,
   * meme s'il est deja ouvert et que l'agent avait bascule manuellement d'onglet.
   */
  openTick: number;
  /**
   * Brouillon a pre-remplir dans le panneau a l'ouverture (ex : "A propos de X : ..."
   * depuis la fiche contact). null = pas de pre-remplissage. Le panneau ne l'applique
   * qu'au changement d'openTick, pour ne pas ecraser une saisie en cours.
   */
  prefill: string | null;
  /**
   * Briefing a jouer a l'ouverture (bouton « Fais appel a Juliette »). Comme
   * `prefill`, n'est applique qu'au changement d'openTick — consomme une fois.
   */
  kickoff: JulietteKickoff | null;
  open: (opts?: { tab?: JuliettePanelTab; prefill?: string; kickoff?: JulietteKickoff }) => void;
  close: () => void;
  /** Conscience de page : role + proactivite de Juliette sur l'ecran courant. */
  context: JulietteContext;
}

const JulietteLauncherContext = createContext<JulietteLauncherState | null>(null);

export function JulietteLauncherProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [panelTab, setPanelTab] = useState<JuliettePanelTab>(DEFAULT_TAB);
  const [openTick, setOpenTick] = useState(0);
  const [prefill, setPrefill] = useState<string | null>(null);
  const [kickoff, setKickoff] = useState<JulietteKickoff | null>(null);
  const { pathname } = useLocation();
  // Le mode est vestigial (seul "panel" subsiste) mais conserve : JulietteRightPanel
  // ne s'affiche que si mode === "panel".
  const mode: JulietteLauncherMode = "panel";

  const open = useCallback(
    (opts?: { tab?: JuliettePanelTab; prefill?: string; kickoff?: JulietteKickoff }) => {
      setPanelTab(opts?.tab ?? DEFAULT_TAB);
      setPrefill(opts?.prefill ?? null);
      setKickoff(opts?.kickoff ?? null);
      setOpenTick((t) => t + 1);
      setIsOpen(true);
    },
    [],
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const context = useMemo<JulietteContext>(
    () => getJuliettePageContext(pathname),
    [pathname],
  );

  const value = useMemo<JulietteLauncherState>(
    () => ({ isOpen, mode, panelTab, openTick, prefill, kickoff, open, close, context }),
    [isOpen, panelTab, openTick, prefill, kickoff, open, close, context],
  );

  return (
    <JulietteLauncherContext.Provider value={value}>
      {children}
    </JulietteLauncherContext.Provider>
  );
}

export function useJulietteLauncher(): JulietteLauncherState {
  const ctx = useContext(JulietteLauncherContext);
  if (!ctx) {
    throw new Error(
      "useJulietteLauncher must be used within <JulietteLauncherProvider>",
    );
  }
  return ctx;
}
